import React, { useEffect, useState } from 'react'
import BreadCrumbs from '../../../components/BreadCrumbs';
import ButtonComponent from '../../../components/ButtonComponent';
import { Button, Card, CardBody, CardHeader } from 'react-bootstrap';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import DataTable, { TableColumn } from 'react-data-table-component';
import ToggleSwitch from '../../../components/ToggleSwitch';
import ViewButtonComponent from '../../../components/ViewButtonComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useDeletePromotionMutation, useLazyGetPromotionListQuery, useUpdatePromotionAvailabilityStatusMutation } from '../../../redux/services/promotionService';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { DEFAULT_IMAGE_URL } from '../../../constants/constans';
import { toast } from 'react-toastify';
import { Badge } from 'reactstrap';
import { useLazyGetMenuItemsByRestaurantIdQuery } from '../../../redux/services/menuItemService';
import { useLazyGetMenuListByRestaurantIdQuery } from '../../../redux/services/setMenuService';
import { useLazyFetchCustomMenuListQuery } from '../../../redux/services/customizeMenuService';
import { MenuItem, setSingleItems } from '../../../redux/menuSlice';
import { setComboItems, SetMenu } from '../../../redux/setMenuSlice';
import { CustomMenuItem, setCustomItems } from '../../../redux/customizeMenuSlice';

export interface Promotion {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    isActive: boolean;
    type: "Fixed" | "Percentage" | "LoyaltyPoints";
    value: number;
    code: string;
    createdBy: {
        id: string;
        name: string;
        role: string;
    };
    createdAt: {
        _seconds: number,
        _nanoseconds: number
    }
    updatedAt: string;
    expiresAt: {
        _seconds: number,
        _nanoseconds: number
    }
    status: string;
}


const Promotions = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [deletingMenuId, setDeletingMenuId] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [singleMenuItemsList, setSingleMenuItemsList] = useState<MenuItem[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [comboItemList, setComboItemList] = useState<SetMenu[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [customMenuItemList, setCustomMenuItemList] = useState<CustomMenuItem[]>([]);

    const selectedRestaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );
    const restaurantId = selectedRestaurantId;

    const handlePromotionDetails = (promotion: Promotion) => {
        navigate("/configuration/promotions/edit-promotion", { state: { promotion } });
    };

    const handleAddNewPromotion = () => {
        navigate("/configuration/promotions/add-promotion");
    };

    const [deletePromotion] = useDeletePromotionMutation();

    const handleDelete = async (id: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#590B21",
            cancelButtonColor: "#ddd",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setDeletingMenuId(id);
                try {
                    await deletePromotion({ restaurantId, promotionId: id }).unwrap();
                    Swal.fire("Deleted!", "Your menu has been deleted.", "success");
                    trigger(restaurantId);
                    setDeletingMenuId(null);
                } catch (error) {
                    Swal.fire(
                        "Error!",
                        "An error occurred while deleting the menu.",
                        "error"
                    );
                    setDeletingMenuId(null);
                }
            }
        });
    };

    const [updateAvailabilityStatus] = useUpdatePromotionAvailabilityStatusMutation();

    const handleToggleChange = async (menuId: string, status: boolean) => {
        try {
            await updateAvailabilityStatus({
                restaurantId,
                promotionId: menuId,
                isActive: status,
            }).unwrap();
            trigger(restaurantId);
        } catch (error) {
            toast.error("Error updating availability status");
        }
    };

    const [trigger, { data: promotionDetails, isLoading }] =
        useLazyGetPromotionListQuery();

    useEffect(() => {
        if (selectedRestaurantId) {
            trigger(selectedRestaurantId);
        }
        if (promotionDetails) {
            setPromotions(promotionDetails);
        }
    }, [selectedRestaurantId, trigger, promotionDetails]);

    const [menuItemTrigger, { data: getMenuItemsList}] = useLazyGetMenuItemsByRestaurantIdQuery();
    const [comboItemTrigger, { data: getComboItemList}] = useLazyGetMenuListByRestaurantIdQuery();
    const [customizeMenuItemTrigger, { data: getCustomizeItemList}] = useLazyFetchCustomMenuListQuery();

    useEffect(() => {
        if (selectedRestaurantId) {
            trigger(selectedRestaurantId);
            menuItemTrigger(selectedRestaurantId);
            comboItemTrigger(selectedRestaurantId);
            customizeMenuItemTrigger(selectedRestaurantId);
        }
    }, [
        comboItemTrigger,
        customizeMenuItemTrigger,
        menuItemTrigger,
        selectedRestaurantId,
        trigger,
    ]);

    useEffect(() => {
        if (getMenuItemsList) {
            const items: MenuItem[] =
                getMenuItemsList?.flatMap((category: any) => category.items) || [];
            setSingleMenuItemsList(items);
            dispatch(setSingleItems(items));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getMenuItemsList]);

    useEffect(() => {
        if (getComboItemList) {
            setComboItemList(getComboItemList?.setMenuList);
            dispatch(setComboItems(getComboItemList?.setMenuList));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getComboItemList]);

    useEffect(() => {
        if (getCustomizeItemList) {
            setCustomMenuItemList(getCustomizeItemList?.customMenuList);
            dispatch(setCustomItems(getCustomizeItemList?.customMenuList));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCustomizeItemList]);

    const columns: TableColumn<Promotion>[] = [
        {
            name: <h6 className="fw-bold">Image</h6>,
            cell: (row) => (
                <img
                    src={row.imageUrl || DEFAULT_IMAGE_URL}
                    alt=""
                    className="set-menu-image"
                />
            ),
        },
        {
            name: <h6 className="fw-bold">Title</h6>,
            selector: (row) => row.title,
        },
        {
            name: <h6 className="fw-bold">Description</h6>,
            selector: (row) => row.description,
        },
        {
            name: <h6 className="fw-bold">Type</h6>,
            selector: (row) => row.type,
        },
        {
            name: <h6 className="fw-bold">Amount</h6>,
            selector: (row) => row.value,
        },
        {
            name: <h6 className="fw-bold">Expiring Date</h6>,
            selector: (row) => {
                const dateRaw = row.expiresAt;

                let dateObj: Date;

                if (typeof dateRaw === "string") {
                    dateObj = new Date(dateRaw);
                } else if (dateRaw?._seconds) {
                    dateObj = new Date(dateRaw._seconds * 1000);
                } else {
                    return "-";
                }
                return dateObj.toISOString().split("T")[0];
            },
        },
        {
            name: <h6 className="fw-bold">Status</h6>,
            cell: (row) => <div>
                <Badge color={row?.status === "Active" ? "success" : row?.status === "Deleted" ? "danger" : "secondary"} className="px-4 py-2">
                    <div style={{ fontSize: 12 }}>{row.status}</div>
                </Badge>
            </div>,
        },
        {
            name: <h6 className="fw-bold">Availability</h6>,
            cell: (row: Promotion) => (
                <ToggleSwitch
                    initialChecked={row.isActive}
                    menuId={row.id}
                    onChange={handleToggleChange}
                />
            ),
        },
        {
            name: <h6 className="fw-bold">Action</h6>,
            cell: (row) => (
                <div className="d-flex gap-2">
                    <ViewButtonComponent onClick={() => handlePromotionDetails(row)} />
                    {deletingMenuId === row.id ? (
                        <Button variant="light" disabled={true}>
                            <FontAwesomeIcon icon={faSpinner} spin />
                        </Button>
                    ) : (
                        <Button variant="light" onClick={() => handleDelete(row.id)}>
                            <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div><BreadCrumbs hasSubMenus={true} />
            <div className="menus-container">
                <div className="d-flex justify-content-end w-100">
                    <ButtonComponent titel="Add" onClick={handleAddNewPromotion} isDisabled={isLoading}/>
                </div>
                <Card className="menus-card">
                    <CardHeader className="menus-card-header">
                        <h4>Promotion List</h4>
                    </CardHeader>
                    <CardBody>
                        {isLoading ? (
                            <TableLoadingComponent numColumns={5} />
                        ) : (
                            <DataTable
                                columns={columns}
                                data={promotions}
                                responsive
                                pagination
                                highlightOnHover
                            />
                        )}
                    </CardBody>
                </Card>
            </div></div>
    )
}

export default Promotions;